import Head from 'next/head';
import { GetStaticPaths, GetStaticProps } from 'next/types';
import { container, Registry } from '@/infra/container-registry';
import { http } from '@/infra/http';
import { BlogPost, CategoryId } from '@/domain/entities/post';
import { ListPostsUseCase } from '@/application/post/list-posts.use-case';
import { categories, categoriesIds, categoriesSlugs } from '@/helpers/categories';
import Header from '@/components/Header';
import CategoryPostsList from '@/components/CategoryPostsList';
import CategoriesSection from '@/components/CategoriesSection';

import { CategoryPrev, CategoryTag, Content, TitleContainer } from '@/styles/pages/category';

type PostProps = {
  posts: Array<BlogPost>;
  category: { name: string; color: string; id: CategoryId };
  postsCount: number;
};

export default function Category({ posts, category, postsCount }: PostProps) {
  return (
    <>
      <Head>
        <title>Posts sobre {category.name as string} | Kenzie Academy</title>
        <meta name="description" content={``} />
        <meta name="twitter:title" content="Kenzie Academy Brasil: Curso de Programação Online" />
        <meta
          name="twitter:description"
          content="Torne-se um desenvolvedor em 12 meses com o curso de programação online da Kenzie Academy Brasil e comece a pagar após estar empregado. Confira!"
        />
        <meta property="og:title" content="Kenzie Academy Brasil: Curso de Programação Online" />
        <link rel="canonical" href={`https://kenzie.com.br/blog/categorias/${category.name}`} />
      </Head>
      <Header />
      <main>
        <Content>
          <TitleContainer>
            <CategoryPrev>Categoria</CategoryPrev>
            <CategoryTag background={category.color}>{category.name}</CategoryTag>
          </TitleContainer>
          <CategoryPostsList posts={posts} categoryId={category.id} postsCount={postsCount} />
        </Content>
        <CategoriesSection />
      </main>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = categoriesSlugs.map((category: string) => ({
    params: { slug: category },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async context => {
  const categorySlug = context.params?.slug as string;
  if (!categoriesSlugs.includes(categorySlug)) {
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
    };
  }
  const categoryId: CategoryId = categoriesIds[categorySlug];
  const category = categories[categoryId];

  const listPostsUseCase = container.get<ListPostsUseCase>(Registry.ListPostsUseCase);
  const posts = await listPostsUseCase.execute({ categories: categoryId, per_page: 12 });

  const postsCount = await http
    .get(`/wp/v2/categories/${categoryId}`)
    .then(response => response.data.count);

  return {
    props: {
      posts: posts,
      category: { ...category, categoryId: categoryId },
      postsCount: postsCount,
    },
  };
};
